import { useState } from "react";
import { venues } from "../data";
import { StripeCardForm } from "./StripeCardForm";
import { PassPurchaseModal } from "./PassPurchaseModal";
import { API, formatTime, toLocalDateStr } from "./calendarUtils";

export function EventModal({ event, date, onClose }) {
  const venue     = venues.find(v => v.id === event.venue);
  const isOnline  = event.venue === "venue_online";
  const humanDate = date.toLocaleDateString("en-US", {
    weekday:"long", month:"long", day:"numeric", year:"numeric"
  });

  const [payMode, setPayMode]                     = useState("dropin");
  const [name, setName]                           = useState("");
  const [email, setEmail]                         = useState("");
  const [passCode, setPassCode]                   = useState("");
  const [codeStatus, setCodeStatus]               = useState(null);
  const [passData, setPassData]                   = useState(null);
  const [checking, setChecking]                   = useState(false);
  const [booking, setBooking]                     = useState(false);
  const [bookError, setBookError]                 = useState(null);
  const [booked, setBooked]                       = useState(false);
  const [waiverSigned, setWaiverSigned]           = useState(false);
  const [passHint, setPassHint]                   = useState(null);
  const [showPurchaseModal, setShowPurchaseModal] = useState(false);

  const base       = window.location.pathname.startsWith('/demo') ? '/demo' : '';
  const nameValid  = name.trim().length > 0;
  const emailValid = /^[^\s@]+@[^\s@]+\.[^\s@]+$/.test(email.trim());
  const canBook    = nameValid && emailValid && (payMode === "dropin" || codeStatus === "valid");

  const handleVerifyCode = async (codeOverride) => {
    const code = codeOverride ?? passCode;
    if (!code.trim()) return;
    setChecking(true);
    setCodeStatus(null);
    setPassData(null);
    try {
      const res  = await fetch(`${API}/passes/verify`, {
        method: "POST",
        headers: { "Content-Type": "application/json" },
        body: JSON.stringify({ input: code.trim() }),
      });
      const data = await res.json();
      if (data.valid) { setCodeStatus("valid"); setPassData(data.pass); }
      else { setCodeStatus("invalid"); }
    } catch { setCodeStatus("invalid"); }
    finally { setChecking(false); }
  };

  const handleEmailBlur = async () => {
    const trimmedEmail = email.trim();
    if (!trimmedEmail) return;
    try {
      const res  = await fetch(`${API}/waiver/status?email=${encodeURIComponent(trimmedEmail)}`);
      const data = await res.json();
      setWaiverSigned(data.waiverSigned ?? false);
    } catch { /* silent */ }
    if (payMode !== "pass") return;
    try {
      const res  = await fetch(`${API}/passes/active?email=${encodeURIComponent(trimmedEmail)}`);
      const data = await res.json();
      if (data.pass) {
        setPassHint(data.pass);
        setPassCode(data.pass.code);
        await handleVerifyCode(data.pass.code);
      }
    } catch { /* silent */ }
  };

  const handleDropinSuccess = async (paymentIntentId) => {
    const bookRes  = await fetch(`${API}/bookings`, {
      method: "POST",
      headers: { "Content-Type": "application/json" },
      body: JSON.stringify({
        name: name.trim(), email: email.trim(),
        classId: event.id, date: toLocalDateStr(date),
        paymentType: "dropin", stripePaymentId: paymentIntentId,
      }),
    });
    const bookData = await bookRes.json();
    if (!bookRes.ok) { setBookError(bookData.message ?? bookData.error ?? "Booking failed. Please try again."); return; }
    setBooked(true);
  };

  const handlePassBook = async () => {
    if (!canBook || booking) return;
    setBooking(true);
    setBookError(null);
    try {
      const bookRes  = await fetch(`${API}/bookings`, {
        method: "POST",
        headers: { "Content-Type": "application/json" },
        body: JSON.stringify({
          name: name.trim(), email: email.trim(),
          classId: event.id, date: toLocalDateStr(date),
          paymentType: passData?.type ?? "pass4",
          passId: passData?.id ?? null,
        }),
      });
      const bookData = await bookRes.json();
      if (!bookRes.ok) { setBookError(bookData.message ?? bookData.error ?? "Booking failed. Please try again."); return; }
      setBooked(true);
      const refreshRes  = await fetch(`${API}/passes/verify`, {
        method: "POST",
        headers: { "Content-Type": "application/json" },
        body: JSON.stringify({ input: passCode.trim() }),
      });
      const refreshData = await refreshRes.json();
      if (refreshData.valid && refreshData.pass) setPassData(refreshData.pass);
    } catch { setBookError("Something went wrong. Please try again."); }
    finally { setBooking(false); }
  };

  if (booked) {
    return (
      <div className="modal-overlay" onClick={onClose}>
        <div className="modal-card booking-modal" onClick={e => e.stopPropagation()}>
          <button className="modal-close" onClick={onClose}>✕</button>
          <div className="booking-success">
            <div className="booking-success-icon">✓</div>
            <h2 className="booking-success-title">You're booked!</h2>
            <p className="booking-success-sub">
              {isOnline ? "A Zoom link will be sent to your email before class." : `See you at ${venue?.name}.`}
            </p>
            {passData && (
              <p className="booking-success-sub" style={{marginTop:"0.5rem",fontSize:"0.85rem",opacity:0.75}}>
                {passData.classesRemaining > 0
                  ? `${passData.classesRemaining} class${passData.classesRemaining !== 1 ? "es" : ""} remaining on your pass.`
                  : "That was your last class on this pass."}
              </p>
            )}
            <div className={`booking-class-summary ${isOnline ? "summary-online" : "summary-studio"}`} style={{marginTop:"1.25rem"}}>
              <span className="summary-title">{event.title}</span>
              <span className="summary-meta">{humanDate} · {formatTime(event.time)}</span>
            </div>
            <button className="book-btn" style={{marginTop:"1.25rem"}} onClick={onClose}>Done</button>
          </div>
        </div>
      </div>
    );
  }

  return (
    <div className="modal-overlay" onClick={onClose}>
      <div className="modal-card booking-modal" onClick={e => e.stopPropagation()}>
        <button className="modal-close" onClick={onClose}>✕</button>
        <div className={`modal-badge ${isOnline ? "badge-online" : "badge-studio"}`}>
          {isOnline ? "Online" : "In Studio"}
        </div>
        <h2 className="modal-title">{event.title}</h2>
        <div className="modal-meta" style={{marginBottom:"1.25rem"}}>
          <div className="meta-row"><span className="meta-icon">📅</span><span>{humanDate}</span></div>
          <div className="meta-row"><span className="meta-icon">🕐</span><span>{formatTime(event.time)} · {event.duration} min</span></div>
          <div className="meta-row"><span className="meta-icon">👤</span><span>{event.teacher}</span></div>
          <div className="meta-row"><span className="meta-icon">📍</span>
            <span>{isOnline ? "Online — link sent on booking" : venue?.address}</span>
          </div>
        </div>

        <div className="booking-fields" style={{marginBottom:"1.25rem"}}>
          <label className="form-label">Full Name
            <input className="form-input" value={name} onChange={e => setName(e.target.value)}
              placeholder="e.g. Jane Smith" minLength={2} required />
          </label>
          <label className="form-label">Email
            <input className="form-input" type="email" value={email}
              onChange={e => { setEmail(e.target.value); setCodeStatus(null); }}
              onBlur={handleEmailBlur} placeholder="your@email.com" />
          </label>
        </div>

        {!waiverSigned && email && (
          <p className="em-waiver-note">
            Before your first class, please read and sign our{" "}
            <a href={`${base}/waiver?email=${encodeURIComponent(email)}&name=${encodeURIComponent(name)}`}
              target="_blank" rel="noreferrer" className="em-waiver-link">liability waiver</a>.
          </p>
        )}
        {waiverSigned && <p className="em-waiver-signed">✓ Liability waiver on file</p>}

        <div className="pay-mode-tabs pay-mode-tabs--three">
          <button className={`pay-mode-tab ${payMode === "dropin" ? "active" : ""}`}
            onClick={() => { setPayMode("dropin"); setCodeStatus(null); setPassData(null); }}>
            Drop-in
            {payMode === "dropin" && <span className="pay-default-badge">default</span>}
          </button>
          <button className={`pay-mode-tab ${payMode === "pass" ? "active" : ""}`}
            onClick={() => setPayMode("pass")}>
            Pass / Membership
          </button>
          <button type="button" className="pay-mode-tab pay-mode-buy"
            onClick={() => setShowPurchaseModal(true)}>
            Buy a Pass
          </button>
        </div>

        {payMode === "dropin" && !showPurchaseModal && (
          <div className="stripe-block">
            <p className="stripe-hint">
              Have a pass or membership?
              <button className="stripe-pass-link" onClick={() => setPayMode("pass")}>Use it instead →</button>
            </p>
            <StripeCardForm
              amount={event.price * 100}
              email={email.trim()}
              billingDetails={{ email: email.trim() }}
              onSuccess={handleDropinSuccess}
              onError={(msg) => setBookError(msg)}
              submitLabel={`Pay $${event.price} & Book`}
              disabled={!nameValid || !emailValid}
            />
            {bookError && <p className="pass-feedback invalid" style={{marginTop:"0.75rem"}}>{bookError}</p>}
          </div>
        )}

        {payMode === "pass" && (
          <div className="pass-block">
            <p className="pass-hint">Enter your pass code (e.g. YOGA-A3F2) or the email on your membership.</p>
            {passHint && (
              <p className="em-pass-hint">
                ✓ Found your pass — {passHint.classesRemaining} class{passHint.classesRemaining !== 1 ? "es" : ""} remaining
              </p>
            )}
            <div className="pass-input-row">
              <input
                className={`form-input pass-input ${codeStatus === "invalid" ? "input-error" : ""} ${codeStatus === "valid" ? "input-valid" : ""}`}
                value={passCode}
                onChange={e => { setPassCode(e.target.value); setCodeStatus(null); setPassData(null); }}
                placeholder="YOGA-XXXX or email address"
                onKeyDown={e => e.key === "Enter" && handleVerifyCode()}
              />
              <button className="pass-verify-btn"
                disabled={!passCode.trim() || checking || !emailValid}
                onClick={handleVerifyCode}>
                {checking ? "…" : "Check"}
              </button>
            </div>
            {!emailValid && passCode.trim() && (
              <p className="pass-feedback invalid" style={{fontSize:"0.8rem"}}>Enter your email above before checking your pass.</p>
            )}
            {codeStatus === "valid" && (
              <p className="pass-feedback valid">
                ✓ Pass confirmed
                {passData?.classesRemaining != null
                  ? ` · ${passData.classesRemaining} class${passData.classesRemaining !== 1 ? "es" : ""} remaining`
                  : ""}.
              </p>
            )}
            {codeStatus === "invalid" && (
              <div className="pass-feedback invalid">
                <p>That code or email wasn't recognised.</p>
                <button className="pass-buy-link" onClick={() => setShowPurchaseModal(true)}>Buy a pass or membership →</button>
              </div>
            )}
            {bookError && <p className="pass-feedback invalid" style={{marginTop:"0.75rem"}}>{bookError}</p>}
            <button className="book-btn" style={{marginTop:"1.25rem"}}
              disabled={!canBook || booking} onClick={handlePassBook}>
              {booking ? "Booking…" : "Confirm Booking"}
            </button>
          </div>
        )}

          {showPurchaseModal && (
            <PassPurchaseModal
              onClose={() => setShowPurchaseModal(false)}
              prefillName={name}
              prefillEmail={email}
              onPurchaseComplete={(code) => {
                setShowPurchaseModal(false);
                setPayMode("pass");
                if (code) {
                  setPassCode(code);
                  handleVerifyCode(code);
                }
              }}
            />
          )}
      </div>
    </div>
  );
}
